import { useMemo } from "react"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import Link from "@/v2-backoffice-client/components/link"
import ManualLink from "@/v2-backoffice-client/components/manual/ManualLink"
import Tabs from "@/v2-backoffice-client/components/tabs"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import {
  CUSTOMER_VIEW_TAB_PARAM,
  CUSTOMER_VIEW_TABS_ID,
} from "@/v2-backoffice-client/customer/customer.links"
import { useCustomerViewQuery } from "@/v2-backoffice-client/customer/view/customer.view.query"
import CustomerViewInfo, {
  type CustomerLicenseRef,
} from "@/v2-backoffice-client/customer/view/CustomerViewInfo"
import CustomerViewToolbar from "@/v2-backoffice-client/customer/view/CustomerViewToolbar"
import CustomerTenant from "@/v2-backoffice-client/customer/tenant/CustomerTenant"
import CustomerUser from "@/v2-backoffice-client/customer/user/CustomerUser"
import CustomerLicense from "@/v2-backoffice-client/customer/license/CustomerLicense"
import CustomerAddOns from "@/v2-backoffice-client/customer/license/CustomerAddOns"
import CustomerPricing from "@/v2-backoffice-client/customer/pricing/CustomerPricing"
import CustomerSeatOveruseNotice from "@/v2-backoffice-client/customer/license/CustomerSeatOveruseNotice"
import CustomerLicenseCoverageNotice from "@/v2-backoffice-client/customer/license/CustomerLicenseCoverageNotice"
import CustomerMergeLeftoverNotice from "@/v2-backoffice-client/customer/merge/CustomerMergeLeftoverNotice"
import { useLicenseAddOnsQuery } from "@/v2-backoffice-client/license/addOns/license.addOns.query"
import CustomerActivity from "@/v2-backoffice-client/customer/activity/CustomerActivity"
import CustomerFeatureAccess from "@/v2-backoffice-client/customer/featureAccess/CustomerFeatureAccess"
import { useCustomerFeatureAccessQuery } from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.query"
import { closedClassCountOf } from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.utils"
import CustomerCollections from "@/v2-backoffice-client/customer/collections/CustomerCollections"
import CustomerBilling from "@/v2-backoffice-client/customer/billing/CustomerBilling"
import { useCustomerBillingQuery } from "@/v2-backoffice-client/customer/billing/customer.billing.query"
import { useLicenseQuery } from "@/v2-backoffice-client/license/license.query"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

function CustomerViewSheet() {
  const { id } = useAppViewSheetOpener()
  const { data, loading, error } = useCustomerViewQuery({
    skip: !id,
    variables: {
      externalId: id ?? "",
    },
  })

  const customer = data?.getCustomerByExternalId

  // License count for the Licensing tab badge. Same query the tab content
  // runs (Apollo dedupes them), and the list is a handful of rows at most —
  // a customer holds one license per product.
  const { data: licenseData, loading: licenseLoading } = useLicenseQuery({
    skip: !customer,
    variables: { customerId: customer?.Id ?? 0 },
  })
  const licenses = licenseData?.getLicenses
  const licenseCount = licenses?.length

  // The license rows for the header's Licenses chips — one per license the
  // customer holds, named after its catalog license (or the product), with
  // its status; the customer row only carries the joined names string.
  const licenseRefs = useMemo<CustomerLicenseRef[] | undefined>(
    () =>
      licenses?.map((license) => ({
        licenseId: license.Id,
        planId: license.PlanId ?? null,
        name: license.PlanName ?? license.ProductName,
        productName: license.ProductName,
        license,
      })),
    [licenses],
  )

  // (AddOnId, AddOnName) pairs for the header's Add-on(s) chips — the same
  // read the Licensing tab's Add-ons section and column run (Apollo dedupes
  // them). Add-ons sit beside the licenses in the header because they are the
  // other half of "what do they have".
  const { data: addOnsData } = useLicenseAddOnsQuery({
    skip: !customer,
    variables: { customerId: customer?.Id ?? 0 },
  })
  const addOnRefs = useMemo(
    () =>
      addOnsData?.getCustomerAddOns.map((addOn) => ({
        id: addOn.AddOnId,
        name: addOn.AddOnName,
        isLive: addOn.HasLiveCarrier,
      })),
    [addOnsData?.getCustomerAddOns],
  )

  // The customer's access as one read — the header's Access line and the
  // What they get tab's badge (its closed classes); the same query the tab
  // content runs (Apollo dedupes them), one row per customer.
  const { data: featureAccessData, loading: featureAccessLoading } =
    useCustomerFeatureAccessQuery({
      skip: !customer,
      variables: { customerId: customer?.Id ?? 0 },
    })
  const featureAccess = featureAccessData?.getCustomerFeatureAccess
  const closedClassCount = featureAccess ? closedClassCountOf(featureAccess) : 0

  // Revenue stats for the Activity tab's churn pricing — same query the
  // Licensing tab content runs (Apollo dedupes them).
  const { data: billingData } = useCustomerBillingQuery({
    skip: !customer,
    variables: { customerId: customer?.Id ?? 0 },
  })
  const billed12M = billingData?.getCustomerRevenueStats?.Billed12M

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={loading}>{customer?.Name}</ViewSheet.Title>
      </ViewSheet.Header>

      <ViewSheet.HeaderRow>
        <CustomerViewInfo
          customer={customer}
          licenseRefs={licenseRefs}
          addOnRefs={addOnRefs}
          featureAccess={featureAccess}
          isLoading={loading}
        />
        <CustomerViewToolbar customer={customer} />
      </ViewSheet.HeaderRow>

      {/* A customer HubSpot merged away is explained before anything else:
          the bare id for a name, the empty CRM card and the team or user that
          belongs elsewhere all follow from it, and the ways on are here. */}
      {customer && <CustomerMergeLeftoverNotice customer={customer} />}

      <Tabs
        id={CUSTOMER_VIEW_TABS_ID}
        defaultValue="tenants"
        isLoading={loading}
        error={error}
        isEmpty={!customer}
        emptyLabel="Customer not found."
      >
        <Tabs.List>
          <Tabs.Trigger
            value="tenants"
            isLoading={loading}
            count={
              (customer?.TenantCount ?? 0) + (customer?.RemovedTenantCount ?? 0)
            }
          >
            Teams
          </Tabs.Trigger>

          <Tabs.Trigger
            value="users"
            isLoading={loading}
            count={
              (customer?.UserCount ?? 0) + (customer?.RemovedUserCount ?? 0)
            }
          >
            Users
          </Tabs.Trigger>

          {/* The two tabs are named by the question each answers, since
              the first is where access is bought and the second where it is
              read and narrowed, and operators lost the thread between them.
              Licenses, add-ons and billing together: "what do they have and
              are they paying for it". Count = licenses (the invoices count
              sits on the table inside). Value stays "billing" so old deep
              links land. */}
          <Tabs.Trigger
            value="billing"
            isLoading={licenseLoading}
            count={licenseCount}
          >
            Licenses &amp; add-ons
          </Tabs.Trigger>

          {/* What the customer's users can use — the package editor through
              the customer lens. The badge is the classes taken "only what's
              listed", shown only when there are any: most customers are Open
              and the tab says so in one sentence. Value stays
              "feature-access" for the same reason. */}
          <Tabs.Trigger
            value="feature-access"
            isLoading={featureAccessLoading}
            count={closedClassCount || undefined}
          >
            What they get
          </Tabs.Trigger>

          {/* The badge counts seats active in the last 28 days (the tab's
              headline stat) — not login sessions, which are short-lived and
              read 0 outside business hours. */}
          <Tabs.Trigger
            value="sign-ins"
            isLoading={loading}
            count={customer?.UsageActiveCount ?? 0}
          >
            Activity
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="tenants">
          {customer && (
            <CustomerTenant
              customerId={customer.Id}
              customerName={customer.Name ?? ""}
              externalCustomerId={customer.ExternalCustomerId ?? undefined}
              activeCount={customer.TenantCount}
              removedCount={customer.RemovedTenantCount}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value="users">
          {customer && (
            <CustomerUser
              customerId={customer.Id}
              activeCount={customer.UserCount}
              removedCount={customer.RemovedUserCount}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value="billing" className="min-h-0 overflow-auto">
          {customer && (
            <div className="flex flex-col gap-6">
              {/* The seat overrun first, while there is one: it is the
                  thing to act on, and the levers are right under it. */}
              <CustomerSeatOveruseNotice
                customer={customer}
                licenses={licenses}
              />
              {/* Where billing, invoicing and access disagree on one of the
                  licenses — a hole, an invoice nothing covers, a trial that
                  ran out — with the bridge and the Fenerum link beside it.
                  Nothing when they agree. */}
              <CustomerLicenseCoverageNotice customerId={customer.Id} />
              {/* Entitlements first (with Grant license), the money story
                  under it — same stacked-sections layout as Activity. */}
              <section className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <h3 className="text-sm font-semibold">Licenses</h3>
                  {/* The manual for the whole job (and what every pill
                      means), one click away from where the licenses are. */}
                  <ManualLink manual="licensing" size="xs" />
                  {/* The other half of the story: the licenses and
                      add-ons decide what the users can use, and that is
                      read on the next tab — say so where the licenses are
                      changed, or the two tabs look unrelated. */}
                  <Link
                    variant="muted"
                    size="xs"
                    iconSrc={null}
                    to={`?${CUSTOMER_VIEW_TAB_PARAM}=feature-access`}
                    className="underline underline-offset-2"
                  >
                    What these let their users do: the What they get tab
                  </Link>
                </div>
                <CustomerLicense
                  customerId={customer.Id}
                  customerName={customer.Name ?? undefined}
                />
              </section>
              {/* The add-ons the customer holds, each with the license it
                  goes with: listed and managed here, right under the
                  licenses, the one place add-ons are set. */}
              <CustomerAddOns
                customerId={customer.Id}
                customerName={customer.Name ?? undefined}
              />
              {/* What the licenses above cost: list against billed per
                  Fenerum subscription, with the deal or contract behind the
                  difference. Renders nothing without live subscriptions. */}
              <CustomerPricing
                customerId={customer.Id}
                billingAccountUuid={customer.BillingAccountUuid}
              />
              {/* Only present while the customer has open collection items —
                  the debt conversation surfaced where the customer is worked,
                  not just on the board. Renders its own section so it can
                  vanish entirely. */}
              <CustomerCollections customerId={customer.Id} />
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold">Billing</h3>
                <CustomerBilling customerId={customer.Id} />
              </section>
            </div>
          )}
        </Tabs.Content>

        <Tabs.Content value="feature-access" className="min-h-0 overflow-auto">
          {customer && (
            <CustomerFeatureAccess
              customerId={customer.Id}
              customerName={customer.Name ?? ""}
              externalCustomerId={customer.ExternalCustomerId ?? undefined}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value="sign-ins" className="min-h-0 overflow-auto">
          {customer && (
            <CustomerActivity
              customer={customer}
              revenueAtStake={
                billed12M != null && billed12M > 0
                  ? {
                      amount: billed12M,
                      currency:
                        billingData?.getCustomerRevenueStats?.PrimaryCurrency ??
                        null,
                    }
                  : null
              }
            />
          )}
        </Tabs.Content>
      </Tabs>
    </>
  )
}

export default CustomerViewSheet
